import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { cn } from '@/lib/cn'
import {
  emptyLegDraft,
  legDraftIncomplete,
  toLegPayload,
  useAddLeg,
  useCase,
} from '@/features/cases/api/useCase'
import type { useApplyDirectionTemplate } from '@/features/cases/api/useCaseLeg'
import {
  DirectionTemplateField,
  LegFields,
  type LegDraft,
} from '@/features/cases/components/CaseRecordSheets'
import { refusalMessage } from '@/features/cases/lib/refusal'
import type { DirectionTemplate } from '@/types/domain'

/* The leg page BEFORE the leg exists — `/cases/:caseId/legs/new` (issue #35).
 *
 * ── Why this is the same page and not its own ────────────────────────────────
 *
 * It was `CaseLegNewPage`, a second route with a second page shell: its own back
 * link, its own title block, its own mobile action bar, its own refusal strip
 * and its own card around the same `LegFields`. The fields were already shared;
 * the FRAME was the copy, and it came with a hundred lines of recovery logic
 * that existed only because a create and a template apply are two writes across
 * two mounted components.
 *
 * `legs/new` is now the same route as `legs/:legId` with the literal in the
 * param, so the create's `navigate(replace)` changes a param instead of swapping
 * one route element for another — and React Router keeps `CaseLegPage` mounted
 * across it. That is what lets the page arm the template apply ONCE, above both
 * desks, and it is the whole reason the recovery machinery could go: a refused
 * copy no longer has to be caught before the surface that fired it unmounts,
 * because the surface that fired it is still there.
 *
 * ── What the reader is left with when the apply is refused ───────────────────
 *
 * The leg, on its own page, with an empty chain, the refusal above it and
 * «Применить направление» in the route card's header. That is the ORDINARY
 * state of a leg nobody has given a corridor yet, plus a sentence — not a
 * special mode with its own retry button and its own vocabulary for a create
 * that half landed. The old page had to say «направление создано, шаблон не
 * применился» because it had nowhere else to put the reader; this one just puts
 * them where the work is.
 *
 * ── The form deliberately does NOT widen ─────────────────────────────────────
 *
 * Exactly what the sheet asked, plus the direction template it already offered.
 * The chain, the trucks and the money are asked on the leg once it exists — and
 * the sections below say so by name rather than by being absent, so the reader
 * can see what this record is about to become.
 *
 * The four actuals (`LegFields`' `actuals`) stay off: an actual departure on a
 * leg nobody has entered yet is not a fact anybody holds.
 */

export function LegDraftDesk({
  caseId,
  applyTemplate,
}: {
  caseId: string
  /** Armed by the PAGE. Passing it in rather than calling the hook here is the
   *  point of the whole shape — see the header. */
  applyTemplate: ReturnType<typeof useApplyDirectionTemplate>
}) {
  /* The case, for the number in the header and for the plain question of
     whether this reader may add anything to it: a 403 or a 404 here is the
     answer, and a create form rendered over one would be an invitation to type
     a leg the server is going to refuse. */
  const { data, isPending, isError, error, refetch } = useCase(caseId)

  if (isPending) {
    return (
      <>
        <Skeleton className="h-7 w-72" />
        <Skeleton className="h-96 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  return <DraftForm caseId={caseId} caseNumber={data.number} applyTemplate={applyTemplate} />
}

function DraftForm({
  caseId,
  caseNumber,
  applyTemplate,
}: {
  caseId: string
  caseNumber: string
  applyTemplate: ReturnType<typeof useApplyDirectionTemplate>
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const addLeg = useAddLeg(caseId)
  const [draft, setDraft, touched, markTouched] = useFormDraft<LegDraft>('new', () =>
    emptyLegDraft(),
  )
  /* The corridor, held BESIDE the draft rather than in it, because the template
     is not part of the leg: applying it is its own call against an id the server
     allocates. A draft carrying it would invite somebody to put it on
     `LegPayload`, which is exactly the reference the copy semantics exist to
     avoid (AD-15). The whole RECORD rather than the id, because the two ends
     come off its points. */
  const [template, setTemplate] = useState<DirectionTemplate | undefined>()

  /* Choosing a corridor fills the leg's two ends from its first and last point,
     and `LegFields` then shows them instead of asking.

     Not a convenience. `replacePoints` calls `SetLegEnds` in the same
     transaction as the copy, so whatever the reader typed into those four
     controls was overwritten a second later by exactly these values — a form
     asking four questions and discarding the answers. Filling them in makes the
     overwrite visible before it happens. */
  const chooseTemplate = (_id: string, picked?: DirectionTemplate) => {
    setTemplate(picked)
    if (!picked) return
    /* `at` rather than the length check that used to stand here: a corridor of
       zero points and one whose ends the array does not actually hold are the
       same nothing to this function, and reading the pair is what proves it. */
    const first = picked.points.at(0)
    const last = picked.points.at(-1)
    if (!first || !last) return
    setDraft({
      ...draft,
      origin: first.name,
      originCountry: first.country,
      destination: last.name,
      destinationCountry: last.country,
    })
  }

  const invalid = legDraftIncomplete(draft)
  const pending = addLeg.isPending

  const submit = () => {
    markTouched()
    if (invalid || pending) return

    addLeg.mutate(toLegPayload(draft), {
      onSuccess: (leg) => {
        /* Fired and NOT awaited, then the navigation goes through. The mutation
           is the page's, so it survives this desk unmounting; if the copy is
           refused, the sentence lands on the leg that now exists, beside the
           control that retries it. Awaiting here would hold the reader on a form
           whose fields no longer describe anything the next submit would save. */
        if (template) applyTemplate.mutate({ legId: leg.id, templateId: template.id })

        /* `replace`, so the browser's Back does not return to a create form that
           has already been used — pressing it would offer to make a SECOND
           direction from a draft the reader believes they submitted once. */
        void navigate(`/cases/${caseId}/legs/${leg.id}`, { replace: true })
      },
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1 className="text-text-heading text-[19px] leading-tight font-normal tracking-[-0.4px]">
            {t('cases.legNew.title')}
            <span className="text-text-muted mx-2 text-[15px] font-light">
              · {caseNumber}
            </span>
          </h1>
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('cases.legNew.subtitle')}
          </p>
        </div>

        {/* ONE instance, placed once. On a phone it detaches into the bar
            pinned to the bottom of the viewport (UX-DR-27); on a desktop it
            sits beside the title. Rendering it twice would give a screen reader
            two "Create" buttons and a test two matches for one control. */}
        <div
          className={cn(
            'flex shrink-0 gap-2',
            'max-md:bg-surface max-md:border-border-subtle max-md:fixed',
            'max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:border-t',
            'max-md:px-4 max-md:py-3',
          )}
        >
          <Button
            variant="secondary"
            size="sm"
            disabled={pending}
            onClick={() => void navigate(`/cases/${caseId}?section=legs`)}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button size="sm" disabled={pending} onClick={submit}>
            {pending ? t('common.form.saving') : t('cases.legNew.create')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('cases.legs.add')}</CardTitle>
          <CardSubtitle>{t('cases.legNew.formSubtitle')}</CardSubtitle>
        </CardHeader>

        {/* A real <form>, so Enter submits from any field exactly as it did in
            the sheet this replaced. `noValidate` because every requirement here
            is stated in the fields' own error slots — the browser's bubbles
            would say the same thing in a second vocabulary. */}
        <form
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            submit()
          }}
          noValidate
        >
          <CardBody className="flex flex-col gap-3.5">
            {/* The refusal renders BESIDE the form with the draft intact
                (UX-DR-18) — which on a page is not a state anything has to hold
                open: nothing dismisses this surface, so a failed save simply
                leaves the reader looking at what they typed with the sentence
                above it.

                The server's refusal wins over the local one: a 409 is more use
                than "fix the fields", and the local complaint reappears on the
                next submit if it still applies. */}
            {addLeg.error ? (
              <p
                role="alert"
                className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
              >
                {refusalMessage(addLeg.error, t)}
              </p>
            ) : touched && invalid ? (
              <p
                role="alert"
                className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
              >
                {t('common.form.fixFields')}
              </p>
            ) : null}

            {/* The corridor leads the form rather than trailing it: it decides
                two of the three required answers, so asking it last would be
                asking the reader to fill in fields it is about to fill for
                them. */}
            <DirectionTemplateField
              value={template?.id ?? ''}
              onChange={chooseTemplate}
            />

            <LegFields
              draft={draft}
              setDraft={setDraft}
              touched={touched}
              lockedEnds={
                template ? t('cases.legNew.endsFromTemplate') : undefined
              }
            />
          </CardBody>
        </form>
      </Card>

      {/* What this record is about to become. NAMED rather than hidden: the
          reader is on the leg's own page already, and three sections that simply
          were not there would make the page they land on after «Создать» look
          like a different screen. Quiet, because none of it can be answered
          yet. */}
      <PendingSection
        title={t('cases.legPage.routeTitle')}
        note={t('cases.legNew.routeLater')}
      />
      <PendingSection
        title={t('cases.legs.vehiclesTable.title')}
        note={t('cases.legNew.vehiclesLater')}
      />
      <PendingSection
        title={t('cases.legFinance.title')}
        note={t('cases.legNew.moneyLater')}
      />
    </>
  )
}

/** A section of the leg page that has nothing to hold until the leg exists. */
function PendingSection({ title, note }: { title: string; note: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-text-faint">{title}</CardTitle>
      </CardHeader>
      <CardBody className="pt-2.5">
        <p className="text-text-muted text-[13px] leading-snug font-light">{note}</p>
      </CardBody>
    </Card>
  )
}
